// Where a visit came from, as the event log keeps it: a campaign code that a
// link carries (`?c=<code>`), and the class of the page before the visit's
// first page (`ref`). Every page script reads them through this module, so
// the Studio's pages and the dock of a game's play page follow one rule.
// Nothing here imports the DOM.
//
// A visit is one tab. Its first page decides the class; a code in an address
// replaces the tab's code. The tab keeps both (sessionStorage, by origin),
// and the links the Studio writes between its own host and the game hosts
// carry them (`c`, and the class as `r`), since a game's address is another
// origin with a store of its own. A page reads the two parameters and takes
// them out of its address, as `?lang` is.
//
// No address is kept: a class is one word of a fixed list, and a code is what
// the person who posted the link wrote into it. And neither is ever recorded
// with a user id: every event that carries them is said for nobody
// (VISIT_EVENTS; `VISIT` in ./events.ts says why).

/** The parameter of a campaign code, and of the class a link carries on. */
export const CAMPAIGN_PARAM = "c";
export const REF_PARAM = "r";
/** Where a tab keeps its visit. */
export const VISIT_KEY = "pocket.visit";

/**
 * The classes of the page before a visit's first page. `pocket` is a page of
 * pocket.nexus or one below it; `none` is no page at all (a typed address, an
 * app, a browser that sends no referrer).
 */
export const REFS = ["x", "reddit", "hn", "youtube", "github", "discord", "search", "pocket", "other", "none"] as const;
export type Ref = (typeof REFS)[number];
export const isRef = (value: unknown): value is Ref => typeof value === "string" && (REFS as readonly string[]).includes(value);

/**
 * The events a page or a play page says with the visit's code and class
 * (`c`, `ref`). Each is recorded with no user id. src/api/events.ts adds the
 * two to each; test/campaign.test.ts holds this list to the catalogue.
 */
export const VISIT_EVENTS: readonly string[] = [
  "landing.viewed",
  "landing.followed",
  "listing.viewed",
  "listing.followed",
  "visit.step",
  "player.seen",
  "player.stayed",
  "player.followed",
];

/**
 * The step of a visit that an event of a person also marks, by that event's
 * name. The event is the person's and says nothing of where the visit came
 * from; the step is said beside it, once, for nobody, with the visit's code
 * and class (`visit.step`). src/api/events.ts says the step whenever a page
 * says the event, so no page script says the two.
 */
export const STEP_OF: Readonly<Record<string, string>> = {
  "signin.shown": "signin",
  "signin.finished": "account",
  "paywall.shown": "paywall",
  "paywall.plan": "plan",
};

/** The rule of an id in the event catalogue (`cleanId` of ./events.ts), in lower case. */
const CODE = /^[a-z0-9][a-z0-9_.:-]{0,47}$/;

/** A campaign code as the log keeps it: lower case, or null for anything that is not one. */
export function campaignCode(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const code = value.trim().toLowerCase();
  return CODE.test(code) ? code : null;
}

const hostOf = (address: string): string => {
  try {
    return new URL(address).hostname.toLowerCase();
  } catch {
    return "";
  }
};
const under = (host: string, domain: string): boolean => host === domain || host.endsWith(`.${domain}`);
/** A development server's hosts: the Studio at a loopback address, a game host one label below `localhost`. */
const local = (host: string): boolean => host === "127.0.0.1" || host === "[::1]" || under(host, "localhost");

/** The class of the page a visitor came from. `referrer` is the browser's, an address or nothing. */
export function refOf(referrer: string): Ref {
  const host = hostOf(referrer);
  if (!host) return "none";
  if (under(host, "pocket.nexus") || local(host)) return "pocket";
  if (under(host, "t.co") || under(host, "x.com") || under(host, "twitter.com")) return "x";
  if (under(host, "reddit.com") || under(host, "redd.it")) return "reddit";
  if (host === "news.ycombinator.com") return "hn";
  if (under(host, "youtube.com") || host === "youtu.be") return "youtube";
  if (under(host, "github.com")) return "github";
  if (under(host, "discord.com") || under(host, "discordapp.com")) return "discord";
  if (/(^|\.)google\.[a-z.]{2,6}$/.test(host) || under(host, "bing.com") || under(host, "duckduckgo.com") || /(^|\.)yahoo\.(com|co\.jp)$/.test(host) || under(host, "baidu.com")) return "search";
  return "other";
}

/**
 * Whether the page before this one is the Studio's own or a game host's, so
 * this page goes on with a visit and does not begin one. `home` is the
 * Studio's host.
 */
export function continues(referrer: string, home: string): boolean {
  const host = hostOf(referrer);
  if (!host) return false;
  return under(host, home.toLowerCase()) || (local(host) && local(home.toLowerCase()));
}

export interface Visit {
  /** The campaign code, or null for a visit with none. */
  c: string | null;
  ref: Ref;
  /** Whether this page is the visit's first: no page of the Studio or of a game came before it in the tab. */
  first: boolean;
}

/** A tab's visit as it is kept: the class, a bar, the code. */
export const keepVisit = (visit: Pick<Visit, "c" | "ref">): string => `${visit.ref}|${visit.c ?? ""}`;

/** What a tab kept, or null for nothing or for anything this module did not write. */
export function keptVisit(kept: string | null): Pick<Visit, "c" | "ref"> | null {
  if (!kept) return null;
  const bar = kept.indexOf("|");
  const ref = bar < 0 ? kept : kept.slice(0, bar);
  if (!isRef(ref)) return null;
  return { ref, c: bar < 0 ? null : campaignCode(kept.slice(bar + 1)) };
}

/** `search` without the parameters named in `keys`; every other parameter stays as it was written. */
function without(search: string, keys: readonly string[]): string {
  if (keys.length === 0 || search.length < 2) return search;
  const left = search
    .slice(1)
    .split("&")
    .filter((pair) => {
      let name: string;
      try {
        name = decodeURIComponent(pair.split("=")[0]!.replace(/\+/g, " "));
      } catch {
        return true;
      }
      return !keys.includes(name);
    });
  return left.length > 0 ? `?${left.join("&")}` : "";
}

/**
 * A page's visit. `search` is the address's query, `referrer` the browser's,
 * `kept` what the tab holds for this origin, `home` the Studio's host. Answers
 * the visit, what the tab is to keep, and the query with a code and a class it
 * read taken out.
 */
export function readVisit(input: { search: string; referrer: string; kept: string | null; home: string }): { visit: Visit; keep: string; search: string } {
  const params = new URLSearchParams(input.search);
  const code = campaignCode(params.get(CAMPAIGN_PARAM));
  const carried = params.get(REF_PARAM);
  const kept = keptVisit(input.kept);
  let visit: Visit;
  // The tab has a visit: its class stays, and a code in the address replaces the tab's.
  if (kept) visit = { c: code ?? kept.c, ref: kept.ref, first: false };
  // A link of the Studio's brought the visit over from another origin.
  else if (isRef(carried)) visit = { c: code, ref: carried, first: false };
  else visit = { c: code, ref: refOf(input.referrer), first: !continues(input.referrer, input.home) };
  const read = [...(code ? [CAMPAIGN_PARAM] : []), ...(isRef(carried) ? [REF_PARAM] : [])];
  return { visit, keep: keepVisit(visit), search: without(input.search, read) };
}

/**
 * `url` with the visit on it, for a link from the Studio's host to a game's
 * address or back. A visit with no code that came from nowhere, or from a
 * page of Pocket's own, adds nothing: the page it leads to reads the same
 * from its referrer. `always` puts the class on all the same, for an address
 * the server answers and no page reads: a package's.
 */
export function withVisit(url: string, visit: Pick<Visit, "c" | "ref">, always = false): string {
  if (!always && !visit.c && (visit.ref === "none" || visit.ref === "pocket")) return url;
  let link: URL;
  try {
    link = new URL(url);
  } catch {
    return url;
  }
  if (visit.c) link.searchParams.set(CAMPAIGN_PARAM, visit.c);
  link.searchParams.set(REF_PARAM, visit.ref);
  // (a parameter with no value reads as its name alone: `?remix`)
  return link.toString().replace(/=(?=&|$)/g, "");
}
